import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createEnvVarSet, listEnvVarSets } from '../utils/savedSecrets';
import {
  getReservedBrokerEnvVarNames,
  hasReservedBrokerEnvVarName,
} from '../utils/reservedEnvVarNames';
import './SavedEnvVarSetSelector.css';

const SavedEnvVarSetSelector = ({
  getApiToken,
  selectedIds,
  setSelectedIds,
  readOnly = false,
  usageLabel = 'backtest',
  onItemsLoaded,
  title = 'Saved variables',
  subtitle,
  allowInlineCreate = true,
  defaultExpanded = true,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState(null);
  const [newVariableKey, setNewVariableKey] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  const [newVariableValueVisible, setNewVariableValueVisible] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');

  const selectedIdSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIdSet.has(item.id)),
    [items, selectedIdSet]
  );
  const selectedSummary = useMemo(() => {
    if (selectedItems.length === 0) {
      return 'No environment variables selected. Most bots do not need these.';
    }
    const variableCount = selectedItems.reduce((count, item) => {
      const keys = Array.isArray(item.keyNames) ? item.keyNames.filter(Boolean) : [];
      return count + Math.max(keys.length, 1);
    }, 0);
    const labels = selectedItems
      .slice(0, 2)
      .map((item) => item.displayName)
      .filter(Boolean);
    const extra = selectedItems.length > 2 ? `, +${selectedItems.length - 2} more` : '';
    if (labels.length === 0) {
      return `${variableCount} variable${variableCount === 1 ? '' : 's'} selected`;
    }
    return `${variableCount} variable${variableCount === 1 ? '' : 's'} selected: ${labels.join(', ')}${extra}`;
  }, [selectedItems]);

  const loadEnvVarSets = useCallback(async () => {
    if (!getApiToken) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken();
      setItems(await listEnvVarSets(token));
    } catch (err) {
      setError(err?.message || 'Failed to load saved variables');
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => {
    loadEnvVarSets();
  }, [loadEnvVarSets]);

  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    if (onItemsLoaded) {
      onItemsLoaded(items);
    }
  }, [items, onItemsLoaded]);

  useEffect(() => {
    const reservedIds = new Set(
      items
        .filter((item) => hasReservedBrokerEnvVarName(item.keyNames))
        .map((item) => item.id)
    );
    if (reservedIds.size === 0 || !Array.isArray(selectedIds) || selectedIds.length === 0) {
      return;
    }
    const nextSelectedIds = selectedIds.filter((id) => !reservedIds.has(id));
    if (nextSelectedIds.length !== selectedIds.length) {
      setSelectedIds(nextSelectedIds);
    }
  }, [items, selectedIds, setSelectedIds]);

  const toggleSelection = (id) => {
    if (readOnly) return;
    const item = items.find((entry) => entry.id === id);
    if (hasReservedBrokerEnvVarName(item?.keyNames)) return;
    const next = new Set(selectedIdSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(Array.from(next));
  };

  const buildVariableLabel = (keyNames = []) => {
    const keys = keyNames.filter(Boolean);
    if (keys.length === 0) {
      return 'No variable name listed';
    }
    if (keys.length === 1) {
      return `Variable: ${keys[0]}`;
    }
    return `Variables: ${keys.join(', ')}`;
  };

  const createSingleVariable = async (event) => {
    event.preventDefault();
    if (readOnly || creating) return;
    const key = newVariableKey.trim().toUpperCase();
    const value = newVariableValue;
    const displayName = newVariableName.trim() || key;
    if (hasReservedBrokerEnvVarName([key])) {
      setError(`${key} is reserved for broker connections. Add the broker connection instead.`);
      return;
    }
    if (!key || !value) {
      setError('Add the variable name and value first.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const token = await getApiToken();
      const item = await createEnvVarSet(token, {
        displayName,
        envVars: { [key]: value },
      });
      setNewVariableKey('');
      setNewVariableValue('');
      setNewVariableValueVisible(false);
      setNewVariableName('');
      setShowCreateForm(false);
      await loadEnvVarSets();
      if (item?.id) {
        const next = new Set(selectedIdSet);
        next.add(item.id);
        setSelectedIds(Array.from(next));
      }
    } catch (err) {
      setError(err?.message || 'Failed to save variable');
    } finally {
      setCreating(false);
    }
  };

  if (!isExpanded && !readOnly) {
    return (
      <div className="saved-env-var-set-selector saved-env-var-set-selector--collapsed">
        <button
          className="saved-env-var-set-selector__collapsed-row"
          type="button"
          onClick={() => setIsExpanded(true)}
        >
          <span className="saved-env-var-set-selector__collapsed-copy">
            <span className="saved-env-var-set-selector__title">{title}</span>
            <span className="saved-env-var-set-selector__collapsed-summary">
              {selectedSummary}
            </span>
          </span>
          <span className="saved-env-var-set-selector__collapsed-action">Show</span>
        </button>
      </div>
    );
  }

  return (
    <div className="saved-env-var-set-selector">
      <div className="saved-env-var-set-selector__header">
        <div>
          <div className="saved-env-var-set-selector__title">{title}</div>
          <div className="saved-env-var-set-selector__subtitle">
            {subtitle || `Pick the saved variables this ${usageLabel} can use.`}
          </div>
        </div>
        {!readOnly && (
          <div className="saved-env-var-set-selector__actions">
            {allowInlineCreate && (
              <Button
                className="saved-env-var-set-selector__add"
                type="button"
                onClick={() => {
                  setIsExpanded(true);
                  setShowCreateForm((current) => !current);
                }}
              >
                {showCreateForm ? 'Cancel' : 'Add variable'}
              </Button>
            )}
          </div>
        )}
      </div>

      {isExpanded && !readOnly && allowInlineCreate && showCreateForm && (
        <Form className="saved-env-var-set-selector__create" onSubmit={createSingleVariable}>
          <div className="saved-env-var-set-selector__create-grid">
            <Form.Group>
              <Form.Label>Variable name</Form.Label>
              <Form.Control
                value={newVariableKey}
                onChange={(event) => setNewVariableKey(event.target.value.toUpperCase())}
                placeholder="GEMINI_API_KEY"
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Value</Form.Label>
              <div className="saved-env-var-set-selector__secret-input">
                <Form.Control
                  className="saved-env-var-set-selector__secret-control"
                  value={newVariableValue}
                  onChange={(event) => setNewVariableValue(event.target.value)}
                  placeholder="Secret value"
                  type={newVariableValueVisible ? 'text' : 'password'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="saved-env-var-set-selector__secret-toggle"
                  onClick={() => setNewVariableValueVisible((visible) => !visible)}
                  aria-label={`${newVariableValueVisible ? 'Hide' : 'Show'} variable value`}
                  aria-pressed={newVariableValueVisible}
                  title={`${newVariableValueVisible ? 'Hide' : 'Show'} value`}
                >
                  <FontAwesomeIcon icon={newVariableValueVisible ? faEyeSlash : faEye} />
                </button>
              </div>
            </Form.Group>
            <Form.Group className="saved-env-var-set-selector__create-name">
              <Form.Label>Nickname</Form.Label>
              <Form.Control
                value={newVariableName}
                onChange={(event) => setNewVariableName(event.target.value)}
                placeholder="Optional"
                maxLength={160}
              />
            </Form.Group>
          </div>
          <div className="saved-env-var-set-selector__create-actions">
            <Button
              type="submit"
              disabled={creating || !newVariableKey.trim() || !newVariableValue}
            >
              {creating ? <><Spinner size="sm" animation="border" /> Saving...</> : 'Save variable'}
            </Button>
          </div>
        </Form>
      )}

      {isExpanded && loading && (
        <div className="saved-env-var-set-selector__status">
          <Spinner size="sm" animation="border" /> Loading saved variables...
        </div>
      )}

      {isExpanded && error && (
        <Alert variant="warning" className="saved-env-var-set-selector__alert">
          {error}
        </Alert>
      )}

      {isExpanded && !loading && !error && items.length === 0 && (
        <div className="saved-env-var-set-selector__empty">
          No environment variables saved yet.
        </div>
      )}

      {isExpanded && !loading && !error && items.length > 0 && (
        <div className="saved-env-var-set-selector__list">
          {items.map((item) => {
            const keyNames = item.keyNames || [];
            const isSelected = selectedIdSet.has(item.id);
            const reservedNames = getReservedBrokerEnvVarNames(keyNames);
            const isReserved = reservedNames.length > 0;
            return (
              <label
                key={item.id}
                className={`saved-env-var-set-selector__item${isSelected ? ' saved-env-var-set-selector__item--selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={readOnly || isReserved}
                  onChange={() => toggleSelection(item.id)}
                />
                <span className="saved-env-var-set-selector__item-main">
                  <span className="saved-env-var-set-selector__item-name">{item.displayName}</span>
                  <span className="saved-env-var-set-selector__item-keys" aria-label={`Saved variables in ${item.displayName}`}>
                    {isReserved
                      ? `Reserved for broker connections: ${reservedNames.join(', ')}`
                      : buildVariableLabel(keyNames)}
                  </span>
                </span>
                {!readOnly && (
                  <span className="saved-env-var-set-selector__item-status">
                    {isReserved ? 'Blocked' : isSelected ? 'Included' : 'Select'}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <div className="saved-env-var-set-selector__footer">
          <button
            className="saved-env-var-set-selector__hide-link"
            type="button"
            onClick={() => setIsExpanded(false)}
          >
            Hide
          </button>
        </div>
      )}
    </div>
  );
};

export default SavedEnvVarSetSelector;
